/**
 * 名片上傳前自動縮圖 + 壓縮
 *
 * 目標：
 * - 長邊 ≤ maxEdge（預設 1600px，足夠 OCR / 螢幕顯示）
 * - 檔案 ≤ maxBytes（預設 800KB）
 * - 輸出 JPEG（體積小、相容佳）
 */

export interface CompressOptions {
  /** 最長邊像素上限，預設 1600 */
  maxEdge?: number;
  /** 目標最大位元組，預設 800KB */
  maxBytes?: number;
  /** 初始 JPEG 品質 0~1，預設 0.85 */
  quality?: number;
  /** 最低品質，預設 0.5 */
  minQuality?: number;
}

export interface CompressResult {
  file: File;
  width: number;
  height: number;
  originalBytes: number;
  compressedBytes: number;
  /** 是否有實際縮圖或降品質 */
  changed: boolean;
}

const DEFAULTS: Required<CompressOptions> = {
  maxEdge: 1600,
  maxBytes: 800 * 1024,
  quality: 0.85,
  minQuality: 0.5,
};

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('無法讀取圖片，請改用 JPG 或 PNG'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('圖片壓縮失敗'));
        else resolve(blob);
      },
      type,
      quality
    );
  });
}

/**
 * 將圖片縮放到 maxEdge 內，並壓到 maxBytes 以下（盡力而為）。
 */
export async function compressCardImage(
  input: File | Blob,
  options: CompressOptions = {}
): Promise<CompressResult> {
  const opts = { ...DEFAULTS, ...options };
  const originalBytes = input.size;
  const originalName = input instanceof File ? input.name : 'card.jpg';

  const img = await loadImage(input);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;

  if (!srcW || !srcH) {
    throw new Error('無法取得圖片尺寸');
  }

  // 計算縮放後尺寸（只縮不放）
  const scale = Math.min(1, opts.maxEdge / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('瀏覽器不支援 Canvas 壓縮');

  // 白底：避免 PNG 透明變成黑底
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  let quality = opts.quality;
  let blob = await canvasToBlob(canvas, 'image/jpeg', quality);

  // 逐步降品質直到符合大小或到底
  while (blob.size > opts.maxBytes && quality > opts.minQuality) {
    quality = Math.max(opts.minQuality, quality - 0.1);
    blob = await canvasToBlob(canvas, 'image/jpeg', quality);
  }

  // 仍過大：再縮一輪（長邊 1200 → 1000）
  if (blob.size > opts.maxBytes) {
    for (const edge of [1200, 1000, 800]) {
      if (blob.size <= opts.maxBytes) break;
      const s = Math.min(1, edge / Math.max(srcW, srcH));
      const w2 = Math.max(1, Math.round(srcW * s));
      const h2 = Math.max(1, Math.round(srcH * s));
      canvas.width = w2;
      canvas.height = h2;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w2, h2);
      ctx.drawImage(img, 0, 0, w2, h2);
      quality = opts.quality;
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      while (blob.size > opts.maxBytes && quality > opts.minQuality) {
        quality = Math.max(opts.minQuality, quality - 0.1);
        blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      }
    }
  }

  const base =
    originalName.replace(/\.[^.]+$/, '').replace(/[^\w\-]+/g, '_').slice(0, 40) || 'card';
  const file = new File([blob], `${base}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });

  return {
    file,
    width: canvas.width,
    height: canvas.height,
    originalBytes,
    compressedBytes: file.size,
    changed: file.size !== originalBytes || scale < 1,
  };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}
